// src/components/layout/LayoutInfoCard.tsx

import { ReactNode, Children, useMemo } from 'react';
import BaseCard from '@/components/ui/BaseCard';
import { Box, Checkbox, Skeleton, SxProps, Theme } from '@mui/material';
import ImagesCarousel from './ImagesCarousel';
import useLazyPreviewInScrollContainer from '../../hooks/components/useLazyPreviewInScrollContainer';
import noPreviewImage from '@/assets/images/no-preview.png';

interface LayoutInfoCardProps {
    id?: string;
    images: string[];
    name: string;
    description: string;
    children: ReactNode;
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    sx?: SxProps<Theme>;
}

function normalizeUrl(url: string | undefined): string {
    if (typeof url !== 'string' || url.trim() === '') {
        return noPreviewImage;
    }
    if (url.startsWith('http://')) {
        return url.replace(/^http:\/\//, 'https://');
    }
    return url;
}

export default function LayoutInfoCard({
    images,
    name,
    children,
    checked,
    onCheckedChange,
    id,
    sx,
}: LayoutInfoCardProps) {
    const chipsNode = Children.toArray(children)[0];
    const actionsNode = Children.toArray(children)[1];
    const normalizedImages = useMemo(() => images.map((url) => normalizeUrl(url)), [images]);
    const { previewRef, shouldRenderPreview } = useLazyPreviewInScrollContainer<HTMLDivElement>();

    const handleCardClick = () => {
        if (onCheckedChange && typeof checked === 'boolean') {
            onCheckedChange(!checked);
        }
    };

    return (
        <BaseCard
            onClick={handleCardClick}
            id={id}
            sx={{
                position: 'relative',
                border: '1px solid #ccc',
                borderRadius: '10px',
                padding: '1rem',
                cursor: onCheckedChange ? 'pointer' : undefined,
                ...sx,
            }}
        >
            <Box
                ref={previewRef}
                sx={{
                    width: '100%',
                    height: 200,
                    mb: 1,
                    overflow: 'hidden',
                    borderRadius: 1,
                }}
            >
                {shouldRenderPreview ? (
                    <ImagesCarousel images={normalizedImages} />
                ) : (
                    <Skeleton variant="rectangular" animation="wave" width="100%" height="100%" />
                )}
            </Box>

            <Box sx={{ mt: 0.3, fontWeight: 'bold' }}>{name}</Box>

            {chipsNode}

            {/* <Box sx={{ height: '42px', mt: 1, overflowY: 'auto' }}>
                <Typography variant="body2">{description}</Typography>
            </Box> */}

            <Box sx={{ mb: 1 }}>{actionsNode}</Box>
            {typeof checked === 'boolean' && onCheckedChange && (
                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                    }}
                >
                    <Checkbox
                        checked={checked}
                        onChange={(_, isChecked) => onCheckedChange(isChecked)}
                        // blow it up a bit:
                        sx={{
                            transform: 'scale(1.4)',
                            padding: '6px',
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.6rem',
                            },
                        }}
                        color="primary"
                    />
                </Box>
            )}
        </BaseCard>
    );
}

function CardChips({ children }: { children: ReactNode }) {
    return <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>{children}</Box>;
}

function CardActions({ children }: { children: ReactNode }) {
    return <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>{children}</Box>;
}

LayoutInfoCard.Chips = CardChips;
LayoutInfoCard.Actions = CardActions;
